/**
 * GIWAHS — Feature Flag Framework (AJIP-16.02.002)
 * ---------------------------------------------------------------------
 * Single, centralised abstraction over every Adaptive Journey feature
 * flag. All flag evaluation MUST flow through this module. Direct
 * `process.env.REACT_APP_JOURNEYS_*` reads elsewhere in the codebase
 * are prohibited by governance.
 *
 *   Master flag ......... JOURNEYS_ENABLED
 *   Sub-flags ........... JOURNEYS_AI
 *                         JOURNEYS_MATCH
 *                         JOURNEYS_PERSISTENCE
 *                         JOURNEYS_ANALYTICS
 *                         JOURNEYS_RECOMMENDATIONS
 *                         JOURNEYS_FACTORY          (added by AJIP-16.02.009, F1)
 *
 * Evaluation rules
 *   • Every flag defaults to FALSE. Missing env values are FALSE.
 *   • Env values are truthy only when set to exactly "true"
 *     (case-insensitive). All other values (undefined, "false", "0",
 *     "no", empty string) evaluate to FALSE.
 *   • Every sub-flag is gated by the master flag. If JOURNEYS_ENABLED
 *     is FALSE, every sub-flag reports FALSE regardless of its own
 *     env value. This is intentional and non-negotiable.
 *   • This module reads env at import time via a frozen snapshot so
 *     runtime toggling is impossible in the browser (mirrors backend).
 *   • Zero side effects. Importing this module never triggers I/O,
 *     network calls, listeners, or DOM work.
 *
 * Public API
 *   FLAGS                     — Frozen map of flag → boolean
 *   FLAG_NAMES                — Frozen array of canonical flag names
 *   isFeatureEnabled(name)    — Pure boolean check
 *   useFeatureFlag(name)      — React hook (stable identity)
 *   getFlagSnapshot()         — Diagnostic-only, returns a copy
 */

const CANONICAL_FLAG_NAMES = Object.freeze([
  'JOURNEYS_ENABLED',
  'JOURNEYS_AI',
  'JOURNEYS_MATCH',
  'JOURNEYS_PERSISTENCE',
  'JOURNEYS_ANALYTICS',
  'JOURNEYS_RECOMMENDATIONS',
  'JOURNEYS_FACTORY',
]);

const ENV_PREFIX = 'REACT_APP_';

const readEnvBool = (name) => {
  const raw = process.env[`${ENV_PREFIX}${name}`];
  if (raw === undefined || raw === null) return false;
  return String(raw).trim().toLowerCase() === 'true';
};

const buildFlagSnapshot = () => {
  const masterOn = readEnvBool('JOURNEYS_ENABLED');
  const snapshot = {};
  for (const name of CANONICAL_FLAG_NAMES) {
    if (name === 'JOURNEYS_ENABLED') {
      snapshot[name] = masterOn;
    } else {
      snapshot[name] = masterOn && readEnvBool(name);
    }
  }
  return Object.freeze(snapshot);
};

const FLAGS = buildFlagSnapshot();
const FLAG_NAMES = CANONICAL_FLAG_NAMES;

const isFeatureEnabled = (name) => {
  if (!Object.prototype.hasOwnProperty.call(FLAGS, name)) {
    // Unknown flag names are always false. Preserves fail-closed posture.
    return false;
  }
  return FLAGS[name] === true;
};

const getFlagSnapshot = () => ({ ...FLAGS });

// React hook is defined lazily so this module can be consumed in pure JS
// contexts (e.g. bootstrap code, node scripts) without pulling React.
let _React = null;
const useFeatureFlag = (name) => {
  if (_React === null) {
    _React = require('react');
  }
  return _React.useMemo(() => isFeatureEnabled(name), [name]);
};

export { FLAGS, FLAG_NAMES, isFeatureEnabled, useFeatureFlag, getFlagSnapshot };
export default { FLAGS, FLAG_NAMES, isFeatureEnabled, useFeatureFlag, getFlagSnapshot };
